import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../components';
import { courseService } from '../services/courseService';
import { topicService } from '../services/topicService';
import PageLayout from '../components/home/PageLayout';
import '../styles/CourseDetailPage.css';

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuthStore();

  const [course, setCourse]                             = useState(null);
  const [topics, setTopics]                             = useState([]);
  const [loading, setLoading]                           = useState(true);
  const [selectedTopics, setSelectedTopics]             = useState(new Set());
  const [isFullCourseSelected, setIsFullCourseSelected] = useState(false);

  /* ── Fetch ──────────────────────────────────────────────────── */
  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) return;
      try {
        setLoading(true);
        const [courseResponse, topicsResponse] = await Promise.all([
          courseService.getCourseDetails(courseId),
          topicService.getTopicsByCourseId(courseId),
        ]);

        const rawCourse = courseResponse?.data;
        let courseData = null;
        if (rawCourse) {
          if (Array.isArray(rawCourse) && rawCourse.length > 0)                   courseData = rawCourse[0];
          else if (Array.isArray(rawCourse?.data) && rawCourse.data.length > 0)   courseData = rawCourse.data[0];
          else                                                                      courseData = rawCourse;
          setCourse(courseData);
        }

        const rawTopics = topicsResponse?.data;
        let topicsData = [];
        if (rawTopics) {
          if (Array.isArray(rawTopics.data))  topicsData = rawTopics.data;
          else if (Array.isArray(rawTopics))   topicsData = rawTopics;
          topicsData = topicsData.map(t => ({ ...t, isPurchase: !!t.isPurchase }));
          setTopics(topicsData);
        }
      } catch (err) {
        console.error('Error fetching course data:', err);
        toast.error('Failed to load course details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchCourseData();
  }, [courseId]);

  /* ── Helpers ────────────────────────────────────────────────── */
  const handleBack = () => navigate('/all-courses');

  const handleEnrollClick = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { returnTo: `/course/${courseId}` } });
      return;
    }
    handleFullCourseSelection();
  };

  const scrollToTopics = () =>
    document.getElementById('course-topics')?.scrollIntoView({ behavior: 'smooth' });

  const handleReadTopic = async (selectedTopic) => {
    try {
      const response = await topicService.getTopicById(selectedTopic.id);
      if (response?.data) {
        const fullTopic = response.data.data;
        const resources = (fullTopic.resources || [])
          .filter(r => r.resourceTypeName === 'HTML')
          .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
        navigate('/reading', {
          state: {
            topic: { ...fullTopic, currentPageIndex: 0, resources, originalStructure: { pageId: 'page-0', totalPages: resources.length, topicId: fullTopic.id, subTopicId: 'default' }, realTopicId: fullTopic.id },
            courseId,
            courseData: { id: course.id, title: course.title, description: course.description, displayPicture: course.displayPicture, topics },
            pdfData: { ...fullTopic, resources },
          },
        });
      }
    } catch {
      toast.error('Failed to load topic. Please try again.');
    }
  };

  const handleTopicSelection = (topicId, e) => {
    e.stopPropagation();
    const next = new Set(selectedTopics);
    next.has(topicId) ? next.delete(topicId) : next.add(topicId);
    setSelectedTopics(next);
    setIsFullCourseSelected(next.size === topics.length && topics.length > 0);
  };

  const handleFullCourseSelection = () => {
    if (isFullCourseSelected) {
      setSelectedTopics(new Set());
      setIsFullCourseSelected(false);
    } else {
      setSelectedTopics(new Set(topics.map(t => t.id)));
      setIsFullCourseSelected(true);
    }
  };

  const calculateSelectedPrice = () =>
    [...selectedTopics].reduce((sum, id) => {
      const t = topics.find(t => t.id === id);
      return sum + (t?.offerPrice || 0);
    }, 0);

  const handleProceedToBilling = () => {
    if (!isFullCourseSelected && selectedTopics.size === 0) {
      toast.error('Please select at least one topic to proceed');
      return;
    }
    if (!course?.id) { toast.error('Invalid course. Please try again.'); return; }
    const selectedList = isFullCourseSelected ? topics : topics.filter(t => selectedTopics.has(t.id));
    navigate('/billing', {
      state: {
        courseId: course.id, courseTitle: course.title,
        coursePrice: course.offerPrice, courseOriginalPrice: course.originalPrice,
        isFullCourse: isFullCourseSelected,
        topics: topics.map(t => ({ id: t.id, title: t.title, offerPrice: t.offerPrice, originalPrice: t.originalPrice })),
        selectedTopics: selectedList.map(t => ({ id: t.id, title: t.title, offerPrice: t.offerPrice, originalPrice: t.originalPrice })),
        totalAmount: isFullCourseSelected ? course.offerPrice : calculateSelectedPrice(),
      },
    });
  };

  /* ── Loading / error ────────────────────────────────────────── */
  if (loading) {
    return (
      <PageLayout>
        <div className="cdp-loading">
          <LoadingSpinner />
          <p>Loading course details…</p>
        </div>
      </PageLayout>
    );
  }

  if (!course) {
    return (
      <PageLayout>
        <div className="cdp-error">
          <p>Course not found.</p>
          <button className="cdp-back-btn" onClick={handleBack}>← Back to Courses</button>
        </div>
      </PageLayout>
    );
  }

  const hasDiscount = course.originalPrice && course.originalPrice !== course.offerPrice && course.originalPrice > 0;
  const discountPct = hasDiscount ? Math.round((1 - course.offerPrice / course.originalPrice) * 100) : 0;
  const sortedTopics = [...topics].sort((a, b) => {
    if (a.orderIndex !== undefined && b.orderIndex !== undefined) return a.orderIndex - b.orderIndex;
    if (a.orderIndex !== undefined) return -1;
    if (b.orderIndex !== undefined) return 1;
    return 0;
  });
  const totalSelectedPrice = isFullCourseSelected ? course.offerPrice : calculateSelectedPrice();
  // Add these variables here
const instructorValue =
  course.instructorName ||
  course.instructor?.name ||
  course.author?.name ||
  (typeof course.instructor === 'string' &&
   !/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(course.instructor)
    ? course.instructor
    : null);

const rawUpdatedAt = course.updatedAt || course.lastUpdated;
const updatedDate = rawUpdatedAt ? new Date(rawUpdatedAt) : null;

const formattedUpdatedDate =
  updatedDate &&
  !Number.isNaN(updatedDate.getTime()) &&
  updatedDate.getFullYear() > 1970
    ? updatedDate.toLocaleDateString('en-IN', {
        month: 'short',
        year: 'numeric'
      })
    : null;

  return (
    <PageLayout>
      {/* cdp-root resets display:flow-root from .sa-page so position:sticky works */}
      <div className="cdp-root">

      <div className="cdp-course-card">
      {/* ══════════════════════════════════════════════════════════
          HERO — animated gradient blob background (no image)
      ══════════════════════════════════════════════════════════ */}
      <section className="cdp-hero">
        {/* animated blob — same as login AnimationBox */}
        <div className="cdp-hero__blob" />
        <div className="cdp-hero__blob cdp-hero__blob--2" />
        {/* dark scrim so text stays readable */}
        <div className="cdp-hero__scrim" />

        <div className="cdp-hero__inner">
          {/* ── LEFT: title + meta ── */}
          <div className="cdp-hero__content">
            <h1 className="cdp-hero__title">{course.title}</h1>

            {course.description && (
              <p className="cdp-hero__desc">
                {course.description.length > 200
                  ? course.description.slice(0, 197) + '…'
                  : course.description}
              </p>
            )}

            <div className="cdp-hero__meta">
              <span className="cdp-meta-chip">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                4 Months Access
              </span>
              {/* Certificate Included chip removed — not applicable */}
              <span className="cdp-meta-chip">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                Expert Faculty
              </span>
            </div>

            {/* price row in hero (mobile / no-sidebar fallback) */}
            <div className="cdp-hero__price-row">
              <span className="cdp-hero__offer-price">
                {course.offerPrice > 0 ? `₹${course.offerPrice}` : 'Free'}
              </span>
              {hasDiscount && (
                <>
                  <span className="cdp-hero__orig-price">₹{course.originalPrice}</span>
                  <span className="cdp-hero__disc-badge">{discountPct}% OFF</span>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════
          BODY — two-column: topics (left) + sticky card (right)
          Info bar lives inside the left column, beside the sidebar
      ══════════════════════════════════════════════════════════ */}
      <div className="cdp-body">

        {/* ── LEFT: info bar + learn box + topics ── */}
        <div className="cdp-body__main" id="course-topics">

          {/* INFO BAR — sits at top of left column, beside sidebar */}
          <div className="cdp-info-bar">
            <div className="cdp-info-bar__inner">

              {/* navy accent block */}
              <div className="cdp-info-bar__accent">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                  <path d="M6 12v5c3 3 9 3 12 0v-5"/>
                </svg>
                <span className="cdp-info-bar__accent-label">
                  {course.isPurchase ? 'Full Access' : 'Premium'}
                </span>
              </div>

              {/* metadata */}
              <div className="cdp-info-bar__meta">
                <div className="cdp-info-meta-row">
                  <span className="cdp-info-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    </svg>
                    <span className="cdp-info-meta-label">Created by</span>
                    <span className="cdp-info-meta-value">H.M Somayya</span>
                  </span>
                  {(course.updatedAt || course.lastUpdated || course.createdAt) && (
                    <span className="cdp-info-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                      <span className="cdp-info-meta-label">Last updated</span>
                      <span className="cdp-info-meta-value">
                        {new Date(course.updatedAt || course.lastUpdated || course.createdAt)
                          .toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                      </span>
                    </span>
                  )}
                  {course.language && (
                    <span className="cdp-info-meta-item">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                      <span className="cdp-info-meta-label">Language</span>
                      <span className="cdp-info-meta-value">{course.language}</span>
                    </span>
                  )}
                  <span className="cdp-info-meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                    </svg>
                    <span className="cdp-info-meta-label">Chapters</span>
                    <span className="cdp-info-meta-value">{sortedTopics.length}</span>
                  </span>
                </div>
                <p className="cdp-info-meta-msg">Structured learning with expert guidance — study at your own pace.</p>
              </div>

              {/* stat blocks */}
              <div className="cdp-info-bar__stats">
                <div className="cdp-info-stat">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                  </svg>
                  <span className="cdp-info-stat__num">{sortedTopics.length}</span>
                  <span className="cdp-info-stat__label">Chapters</span>
                </div>
                {/* Students stat — static for now, will wire to API later */}
                <div className="cdp-info-stat">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span className="cdp-info-stat__num">200+</span>
                  <span className="cdp-info-stat__label">Learners</span>
                </div>
              </div>

            </div>
          </div>
          <div className="cdp-learn-box">
            <h3 className="cdp-learn-box__title">What you'll learn</h3>
            <ul className="cdp-learn-box__list">
              <li>Master core concepts through structured video lessons</li>
              <li>Solve practice problems with step-by-step solutions</li>
              <li>Get guidance from expert faculty members</li>
              <li>Earn a certificate on course completion</li>
            </ul>
          </div>

          {/* topics header */}
          <div className="cdp-topics-header">
            <div>
              <h2 className="cdp-topics-title">Course Topics</h2>
              <div className="cdp-topics-underline" />
            </div>
            {selectedTopics.size > 0 && !isFullCourseSelected && (
              <span className="cdp-selection-badge">
                {selectedTopics.size} topic{selectedTopics.size !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          <p className="cdp-topics-summary">
            {sortedTopics.length} chapter{sortedTopics.length !== 1 ? 's' : ''} · All levels ·
            {sortedTopics.filter(t => t.isPurchase).length > 0
              ? ` ${sortedTopics.filter(t => t.isPurchase).length} unlocked`
              : ' Select topics to enroll'}
          </p>

          {/* topic rows */}
          <div className="cdp-topic-list">
            {sortedTopics.map((topic, index) => {
              const isSelected = selectedTopics.has(topic.id) || isFullCourseSelected;
              return (
                <div
                  key={topic.id}
                  className={`cdp-topic-row ${isSelected ? 'cdp-topic-row--selected' : ''} ${topic.isPurchase ? 'cdp-topic-row--purchased' : ''}`}
                  onClick={e => !topic.isPurchase && handleTopicSelection(topic.id, e)}
                >
                  <div className="cdp-topic-num">
                    {topic.isPurchase
                      ? <span className="cdp-topic-num__icon">✓</span>
                      : <span>{String(index + 1).padStart(2, '0')}</span>
                    }
                  </div>

                  <div className="cdp-topic-info">
                    <span className="cdp-topic-chapter">Chapter {index + 1}</span>
                    <span className="cdp-topic-title">{topic.title}</span>
                  </div>

                  {!topic.isPurchase && (topic.offerPrice || topic.originalPrice) && (
                    <div className="cdp-topic-price">
                      {topic.offerPrice > 0 && <span className="cdp-topic-offer">₹{topic.offerPrice}</span>}
                      {topic.originalPrice && topic.originalPrice !== topic.offerPrice && (
                        <span className="cdp-topic-original">₹{topic.originalPrice}</span>
                      )}
                    </div>
                  )}

                  <div className="cdp-topic-action">
                    {topic.isPurchase ? (
                      <button
                        className="cdp-read-btn"
                        onClick={e => { e.stopPropagation(); handleReadTopic(topic); }}
                      >
                        📖 Read
                      </button>
                    ) : (
                      <button
                        className={`cdp-select-btn ${isSelected ? 'cdp-select-btn--remove' : ''}`}
                        onClick={e => handleTopicSelection(topic.id, e)}
                      >
                        {isSelected ? '− Remove' : '+ Add'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── RIGHT: sticky purchase card ── */}
        <aside className="cdp-sidebar">
          <div className="cdp-buy-card">
            {course.displayPicture && (
              <img
                className="cdp-buy-card__thumb"
                src={course.displayPicture}
                alt={`${course.title} course`}
              />
            )}
            <div className="cdp-buy-card__body">
              {/* price */}
              <div className="cdp-buy-card__price-row">
                <span className="cdp-buy-card__offer">
                  {course.offerPrice > 0 ? `₹${course.offerPrice}` : 'Free'}
                </span>
                {hasDiscount && (
                  <>
                    <span className="cdp-buy-card__orig">₹{course.originalPrice}</span>
                    <span className="cdp-buy-card__disc">{discountPct}% off</span>
                  </>
                )}
              </div>

              {hasDiscount && (
                <p className="cdp-buy-card__urgency">⏰ Limited time offer</p>
              )}

              {/* CTA */}
              {course.isPurchase ? (
                <button className="cdp-buy-card__btn cdp-buy-card__btn--green" onClick={scrollToTopics}>
                  ▶ Start Learning
                </button>
              ) : (
                <button className="cdp-buy-card__btn cdp-buy-card__btn--primary" onClick={handleEnrollClick}>
                  Enroll Now
                </button>
              )}

              {/* guarantees */}
              <ul className="cdp-buy-card__perks">
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  4 months full access
                </li>
                {/* Certificate of completion removed — not applicable */}
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                  Expert faculty support
                </li>
                <li>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                  Unlimited practice attempts
                </li>
              </ul>

              {/* selected topics summary */}
              {(selectedTopics.size > 0 || isFullCourseSelected) && (
                <div className="cdp-buy-card__selection">
                  <div className="cdp-buy-card__sel-label">
                    {isFullCourseSelected
                      ? `Full course · ${topics.length} topics`
                      : `${selectedTopics.size} topic${selectedTopics.size !== 1 ? 's' : ''} selected`}
                  </div>
                  <div className="cdp-buy-card__sel-total">
                    Total: <strong>{totalSelectedPrice > 0 ? `₹${totalSelectedPrice}` : 'Free'}</strong>
                  </div>
                  <button className="cdp-buy-card__proceed" onClick={handleProceedToBilling}>
                    Proceed to Billing →
                  </button>
                </div>
              )}
            </div>
          </div>
        </aside>

      </div>
      </div>
      </div>
    </PageLayout>
  );
};

export default CourseDetailsPage;
